import {
  Dialog,
  DialogContent,
  Typography,
  DialogActions,
} from '@mui/material';
import showToast from '@/utils/toast';
import { MESSAGES } from '@/constants/messages';
import { SVLoader, SVButton } from '../core';
import { useDeactivateBusiness } from '@/hooks/requests/useBusinesses';

type BusinessDeactivateModalProps = {
  id: string;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function BusinessDeactivateModal({
  id,
  open,
  setOpen,
}: BusinessDeactivateModalProps) {
  const {
    canDeactivateBusiness,
    isCanDeactivateBusinessPending,
    deactivateBusiness,
    isDeactivateBusinessPending,
  } = useDeactivateBusiness(id, open);

  if (isCanDeactivateBusinessPending) {
    return (
      <Dialog
        fullWidth
        maxWidth="lg"
        open={open}
        onClose={() => setOpen(false)}>
        <DialogContent>
          <SVLoader />
        </DialogContent>
      </Dialog>
    );
  }

  if (!canDeactivateBusiness) {
    return (
      <Dialog
        fullWidth
        maxWidth="lg"
        open={open}
        onClose={() => setOpen(false)}>
        <DialogContent>
          <Typography variant="h4">Cannot Deactivate Business</Typography>
          <Typography>
            This business cannot be deactivated because they are associated with
            at least one active or locked bank account. Please, close all bank
            accounts associated with the business prior to deactivating the
            business.
          </Typography>
        </DialogContent>
        <DialogActions>
          <SVButton variant="outlined" onClick={() => setOpen(false)}>
            Back
          </SVButton>
        </DialogActions>
      </Dialog>
    );
  }

  async function handleDeactivate() {
    try {
      await deactivateBusiness();
      showToast({
        type: 'success',
        data: { content: 'Business has been successfully deactivated' },
      });
      setOpen(false);
    } catch {
      showToast({ type: 'error', data: { content: MESSAGES.genericError } });
    }
  }

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={() => setOpen(false)}>
      <DialogContent>
        <Typography variant="h4">Deactivate Business?</Typography>
        <Typography>
          Are you sure you would like to deactivate this business?
        </Typography>
      </DialogContent>
      <DialogActions>
        <SVButton variant="outlined" onClick={() => setOpen(false)}>
          No, Don't Deactivate
        </SVButton>
        <SVButton
          variant="error"
          isLoading={isDeactivateBusinessPending}
          onClick={handleDeactivate}>
          Yes, Deactivate
        </SVButton>
      </DialogActions>
    </Dialog>
  );
}
